import Cookies from 'js-cookie';
import { AUTH_COOKIE_NAMES, AuthCookieName, resolveAuthCookieDomain } from '@/lib/constants/auth-cookie';

/** LKUR 수명의 하한. 서버가 refreshDuration 을 주지 않는 경로에서만 쓴다. */
const FALLBACK_ROLE_COOKIE_DAYS = 30;

const authCookieDomain = (): string | undefined => (typeof window === 'undefined' ? undefined : resolveAuthCookieDomain(window.location.hostname));

/**
 * 인증 쿠키를 두 스코프에서 모두 지운다.
 *
 * 2026-04-14(ac3df892) 이전에 발급된 쿠키는 도메인이 없는 host-only 쿠키다.
 * 지금은 '.lawkit.kr' 에 심으므로 그 구 쿠키는 덮어써지지도 지워지지도 않는다.
 * 그런데 js-cookie 의 get 은 document.cookie 의 첫 매치에서 break 하고, 브라우저는
 * 더 구체적인(= 오래된 host-only) 쿠키를 앞에 보낸다. 결국 로그아웃해도 세션이 남고,
 * 재로그인해도 낡은 토큰을 계속 읽는다.
 */
const removeAuthCookieEverywhere = (name: AuthCookieName): void => {
  const domain = authCookieDomain();
  if (domain) Cookies.remove(name, { path: '/', domain });
  Cookies.remove(name, { path: '/' });
};

/** 새 값을 심기 전에 레거시 host-only 잔재를 치운다 — 안 그러면 새 값이 구 값에 가린다. */
const setAuthCookie = (name: AuthCookieName, value: string, expires?: Date | number): void => {
  const domain = authCookieDomain();
  if (domain) Cookies.remove(name, { path: '/' });

  Cookies.set(name, value, {
    expires,
    path: '/',
    domain,
    secure: !!domain,
    sameSite: 'lax',
  });
};

export const storage = {
  local: {
    set: (key: string, value: string) => {
      localStorage.setItem(key, value);
    },
    get: (key: string): string | null => {
      return localStorage.getItem(key);
    },
    getObject: <T>(key: string): T | null => {
      const value = localStorage.getItem(key);
      return value ? JSON.parse(value) : null;
    },
    remove: (key: string) => {
      localStorage.removeItem(key);
    },
    clear: () => {
      localStorage.clear();
    },
  },

  cookie: {
    set: (key: string, value: string, options?: any) => {
      Cookies.set(key, value, options);
    },
    get: (key: string): string | undefined => {
      return Cookies.get(key);
    },
    getObject: <T>(key: string): T | null => {
      const value = Cookies.get(key);
      return value ? JSON.parse(value) : null;
    },
    remove: (key: string) => {
      Cookies.remove(key, { path: '/' });
    },
  },
};

// 편의 함수들 (쿠키 전용)
export const authStorage = {
  setToken: (accessToken: string, refreshToken: string, expireDate: string, refreshDuration: number) => {
    setAuthCookie('LKAT', accessToken, new Date(expireDate.replace(' ', 'T') + '+09:00'));
    setAuthCookie('LKRT', refreshToken, new Date(Date.now() + refreshDuration));
  },
  getAccessToken: () => storage.cookie.get('LKAT'),
  getRefreshToken: () => storage.cookie.get('LKRT'),
  removeAccessToken: () => removeAuthCookieEverywhere('LKAT'),
  removeRefreshToken: () => removeAuthCookieEverywhere('LKRT'),
  tokenClear: () => {
    removeAuthCookieEverywhere('LKAT');
    removeAuthCookieEverywhere('LKRT');
  },
  /**
   * 세션을 통째로 접는다 — 토큰뿐 아니라 역할·이름 힌트까지.
   *
   * tokenClear() 는 LKAT/LKRT 만 지운다. LKUR 이 남으면 헤더는 로그인 상태로 그리는데
   * 서버는 토큰이 없다고 보는, 화면과 서버의 판단이 갈린 상태가 된다 — 예전에 로그인 버튼이
   * 먹통이 되고 모바일이 /sign-in 과 대시보드를 무한 왕복하던 바로 그 조합이다.
   * "이 세션은 끝났다"고 판정한 자리에서는 반드시 이쪽을 쓴다.
   */
  sessionClear: () => {
    for (const name of AUTH_COOKIE_NAMES) removeAuthCookieEverywhere(name);
  },
  /**
   * LKUR 은 화면 분기용 힌트일 뿐이지만, 헤더가 이 값으로 로그인 버튼 노출을 정한다.
   * 30일 고정이던 시절에는 refreshDuration 이 30일보다 길면 LKUR 만 먼저 사라져
   * "로그인 상태인데 헤더에는 로그인 버튼이 보이는" 상태에 빠졌다. 토큰과 같이 늙게 한다.
   */
  setUserRole: (userRole: string, refreshDuration?: number) => {
    setAuthCookie('LKUR', userRole, refreshDuration ? new Date(Date.now() + refreshDuration) : FALLBACK_ROLE_COOKIE_DAYS);
  },
  getUserRole: () => storage.cookie.get('LKUR'),
  removeUserRole: () => removeAuthCookieEverywhere('LKUR'),
  setUserName: (userName: string, refreshDuration?: number) => {
    setAuthCookie('LKUN', userName, refreshDuration ? new Date(Date.now() + refreshDuration) : undefined);
  },
  getUserName: () => storage.cookie.get('LKUN'),
  // 예전에는 domain 없는 remove 만 호출해서 프로덕션에서는 LKUN 이 지워지지 않았다 —
  // 로그아웃을 눌러도 이름이 남는, 조용히 실패하던 경로다.
  removeUserName: () => removeAuthCookieEverywhere('LKUN'),
};

export interface AuthData {
  token: string;
}
